import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Alert, Modal, Spin, Typography } from 'antd';
// API
import { GET_CB_TEAM } from 'api/cbTeam/queries';
import { ADMIN_PREVIEW_CB_TEAM_OWNERSHIP_TRANSFER } from 'api/cbTeamOwnership/queries';
import { ADMIN_TRANSFER_CB_TEAM_OWNERSHIP } from 'api/cbTeamOwnership/mutations';
// Types
import { GetCbTeam, GetCbTeamVariables } from 'api/cbTeam/types/GetCbTeam';
import {
  AdminPreviewCbTeamOwnershipTransfer,
  AdminPreviewCbTeamOwnershipTransferVariables,
} from 'api/cbTeamOwnership/types/AdminPreviewCbTeamOwnershipTransfer';
import {
  AdminTransferCbTeamOwnership,
  AdminTransferCbTeamOwnershipVariables,
} from 'api/cbTeamOwnership/types/AdminTransferCbTeamOwnership';
import {
  AgencyRow,
  CheckResult,
  OwnershipTransferPreview,
  OwnershipTransferResult,
} from 'helpers/teams/cbTeamOwnershipTransfer.types';
// Components
import CurrentOwnerCard from './CurrentOwnerCard/CurrentOwnerCard';
import NewOwnerPicker from './NewOwnerPicker/NewOwnerPicker';
import TransferPreviewSection from './TransferPreviewSection/TransferPreviewSection';
import TransferResultSection from './TransferResultSection/TransferResultSection';
// Helpers
import {
  extractChecksFromError,
  formatEstateSummary,
  mergeFailedChecksIntoPreview,
} from 'helpers/teams/cbTeamOwnershipTransfer.helpers';
// UI
import { errorNotification, successNotification } from 'ui/Notification';
// Styles
import styles from './CbTeamOwnershipTransfer.module.scss';

const { Title, Text } = Typography;

const CbTeamOwnershipTransfer: React.FC = () => {
  const { ownerId } = useParams<{ ownerId: string }>();
  const previewSectionRef = useRef<HTMLDivElement>(null);

  const [selectedTarget, setSelectedTarget] = useState<AgencyRow | null>(null);
  const [reason, setReason] = useState('');
  const [preview, setPreview] = useState<OwnershipTransferPreview | null>(null);
  const [blockedChecks, setBlockedChecks] = useState<CheckResult[] | null>(
    null
  );
  const [transferResult, setTransferResult] =
    useState<OwnershipTransferResult | null>(null);

  const {
    data: teamData,
    loading: teamLoading,
    error: teamError,
    refetch: refetchTeam,
  } = useQuery<GetCbTeam, GetCbTeamVariables>(GET_CB_TEAM, {
    variables: {
      input: {
        ownerId: ownerId || undefined,
      },
    },
    skip: !ownerId,
    fetchPolicy: 'network-only',
  });

  const sourceTeam = teamData?.getCbTeam ?? null;
  const sourceOwnsTeam = Boolean(sourceTeam?.id);
  const canStartPreviewFlow = !teamLoading && !teamError && sourceOwnsTeam;

  const [previewTransfer, { loading: previewLoading }] = useLazyQuery<
    AdminPreviewCbTeamOwnershipTransfer,
    AdminPreviewCbTeamOwnershipTransferVariables
  >(ADMIN_PREVIEW_CB_TEAM_OWNERSHIP_TRANSFER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setPreview(data.adminPreviewCbTeamOwnershipTransfer);
      setBlockedChecks(null);
    },
    onError: (error) => {
      errorNotification(
        error.message || 'Failed to preview ownership transfer'
      );
    },
  });

  const [transferOwnership, { loading: transferLoading }] = useMutation<
    AdminTransferCbTeamOwnership,
    AdminTransferCbTeamOwnershipVariables
  >(ADMIN_TRANSFER_CB_TEAM_OWNERSHIP);

  useEffect(() => {
    setSelectedTarget(null);
    setPreview(null);
    setBlockedChecks(null);
    setTransferResult(null);
    setReason('');
  }, [ownerId]);

  useEffect(() => {
    if (teamLoading || sourceOwnsTeam) {
      return;
    }

    setSelectedTarget(null);
    setPreview(null);
    setBlockedChecks(null);
  }, [teamLoading, sourceOwnsTeam]);

  useEffect(() => {
    if (!preview) {
      return;
    }

    previewSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, [preview]);

  const runPreview = (toOwnerId: string) => {
    if (!ownerId || !toOwnerId) {
      return;
    }

    setTransferResult(null);
    setBlockedChecks(null);
    previewTransfer({
      variables: {
        input: {
          fromOwnerId: ownerId,
          toOwnerId,
        },
      },
    });
  };

  const handlePreviewTarget = (agency: AgencyRow) => {
    if (agency.id === ownerId) {
      errorNotification('Target owner must be different from the source owner');
      return;
    }

    setSelectedTarget(agency);
    runPreview(agency.id);
  };

  const handleTransfer = () => {
    if (!ownerId || !selectedTarget || !preview?.canTransfer) {
      return;
    }

    Modal.confirm({
      title: 'Confirm team ownership transfer',
      content: (
        <div>
          <p>
            Transfer this brand&apos;s team and related data from the current
            owner to{' '}
            <strong>
              {selectedTarget.name || selectedTarget.email || selectedTarget.id}
            </strong>
            ?
          </p>
          <p>{formatEstateSummary(preview.estate)}</p>
          <p>
            Anything already on the new owner&apos;s wallet or talent CRM is
            merged in. To undo later, run the transfer the other way.
          </p>
        </div>
      ),
      okText: 'Transfer ownership',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const { data } = await transferOwnership({
            variables: {
              input: {
                fromOwnerId: ownerId,
                toOwnerId: selectedTarget.id,
                reason: reason.trim() || null,
              },
            },
          });

          const result = data?.adminTransferCbTeamOwnership;
          if (result) {
            setTransferResult(result);
            setBlockedChecks(null);
            successNotification('Team ownership transferred successfully');
            await refetchTeam();
          }
        } catch (error) {
          const checksFromError = extractChecksFromError(error);
          if (checksFromError?.length) {
            setPreview((current) => {
              if (!current) {
                return current;
              }

              const checks = mergeFailedChecksIntoPreview(
                current.checks,
                checksFromError
              );

              return {
                ...current,
                canTransfer: false,
                checks,
              };
            });
            setBlockedChecks(checksFromError);
          } else if (selectedTarget) {
            // Same check list as preview — refresh if the error payload has no checks
            runPreview(selectedTarget.id);
          }

          errorNotification(
            (error as Error)?.message ||
              'Ownership transfer blocked. Review the checks below.'
          );
          throw error;
        }
      },
    });
  };

  const checksToRender = preview?.checks || blockedChecks || [];

  if (!ownerId) {
    return (
      <Alert
        message="Missing brand"
        description="Open this page from Brand Details so the current owner is known."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className={styles.root}>
      <Title level={2}>Transfer Team Ownership</Title>
      <Text type="secondary" className={styles.intro}>
        Moves this brand&apos;s team, billing, wallet, campaigns, talent CRM,
        chats, and plan features from the current owner to another brand
        account. Admin only.
      </Text>

      <CurrentOwnerCard
        ownerId={ownerId}
        sourceOwnsTeam={sourceOwnsTeam}
        teamLabel={sourceTeam?.companyName || sourceTeam?.id}
      />

      {teamLoading && (
        <div className={styles.loadingContainer}>
          <Spin size="large" tip="Checking whether this brand owns a team…" />
        </div>
      )}

      {!teamLoading && teamError && (
        <Alert
          className={styles.statusAlert}
          type="error"
          showIcon
          message="Could not check team ownership"
          description={
            teamError.message ||
            'Failed to load team info for this brand. Try again later.'
          }
        />
      )}

      {!teamLoading && !teamError && !sourceOwnsTeam && (
        <Alert
          className={styles.statusAlert}
          type="warning"
          showIcon
          message="This brand does not own a team"
          description="Team ownership transfer can only start from a brand that already owns a team. Pick a different brand from Brands → Brand Details, or reverse a previous transfer if you need to move ownership back."
        />
      )}

      {canStartPreviewFlow && (
        <NewOwnerPicker
          ownerId={ownerId}
          selectedTarget={selectedTarget}
          previewLoading={previewLoading}
          onPreview={handlePreviewTarget}
        />
      )}

      {canStartPreviewFlow && (preview || blockedChecks) && (
        <TransferPreviewSection
          ref={previewSectionRef}
          selectedTarget={selectedTarget}
          preview={preview}
          checks={checksToRender}
          reason={reason}
          previewLoading={previewLoading}
          transferLoading={transferLoading}
          transferCompleted={Boolean(transferResult)}
          onReasonChange={setReason}
          onRefreshPreview={() =>
            selectedTarget && runPreview(selectedTarget.id)
          }
          onTransfer={handleTransfer}
        />
      )}

      {transferResult && <TransferResultSection result={transferResult} />}
    </div>
  );
};

export default CbTeamOwnershipTransfer;
